import { Box } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar';
import { TabStyled } from 'components/tab/styles';
import { toNumber } from 'lodash';
import { TabsContainerContractsListView } from 'modules/employees/add-edit-employee-modal/styles';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import {
  deleteContractsAction,
  downloadContractAction,
  downloadContractsErrorAction,
  getContractsInListViewAction,
} from 'store/actions/contract-actions';
import { costCentersDropdownItemsSelector } from 'store/selectors/company-cost-centers-selectors';
import { functionsDropdownItemsSelector } from 'store/selectors/company-functions-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import { downloadContractsErrorSelector } from 'store/selectors/contracts-selector';
import { CompanyRole } from 'constants/CompanyRole';
import { ContractListViewTypes } from 'constants/ContractListViewTypes';
import {
  CONTRACTS__ARCHIVED,
  CONTRACTS__COMPLETED_NEW,
  CONTRACTS__EXPORT_FAILED,
  CONTRACTS__FUTURE_CONTRACTS,
  CONTRACTS__TO_BE_CONFIRMED,
  CONTRACTS__TO_BE_PROCESSED,
} from 'translations/constants';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/DownloadGreen.svg';
import { ContractsListViewProps } from '../types';
import ArchivedContractsView from './ArchivedContractsView';
import FutureContractsView from './FutureContractsView';
import ToBeConfirmedContractsView from './ToBeConfirmedContractsView';
import ToBeProcessedView from '../../contract-processing/ToBeProcessedView';
import CompletedView from '../../contract-processing/CompletedView';

const contractsTypes: Record<number, string> = { 0: 'future', 1: 'toconfirm', 2: 'archived', 4: 'archived' };

const ContractsListView = (props: ContractsListViewProps): JSX.Element => {
  const {
    companyId,
    currentWeek,
    currentYear,
    filters,
    setViewType,
    currentTab,
    setCurrentTab,
    locationId,
    workerClass,
    functionId,
    completedWeeks,
  } = props;
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const costCentersList = useAppSelector(costCentersDropdownItemsSelector);
  const functionsList = useAppSelector(functionsDropdownItemsSelector);
  const profile = useAppSelector(profileSelector);
  const downloadContractsError = useAppSelector(downloadContractsErrorSelector);
  const isAdmin = profile.role === CompanyRole.ADMIN;
  const isManagerRole = [CompanyRole.COMPANY_MANAGER, CompanyRole.LOCATION_MANAGER, CompanyRole.HR_MANAGER].includes(
    profile.role,
  );
  const canAccessToBeProcessed = isAdmin || (isManagerRole && profile?.featureTimeRegistrationEnabled);
  const [selectedContractsIds, setSelectedContractsIds] = useState<number[]>([]);

  const onChangeCurrentTab = useCallback(
    (event: SyntheticEvent, value: number) => {
      setCurrentTab(value);
    },
    [setCurrentTab],
  );

  useEffect(() => {
    setSelectedContractsIds([]);
  }, [currentTab]);

  const makeDeleteRequest = (ids: number[], listViewType: ContractListViewTypes) => {
    dispatch(
      deleteContractsAction({
        companyId: toNumber(companyId),
        contractsIds: ids,
        year: currentYear,
        weekNumber: currentWeek,
      }),
    ).then(e => {
      filters.listViewType = listViewType;
      dispatch(
        getContractsInListViewAction({
          companyId: toNumber(companyId),
          filter: filters,
        }),
      );
    });
  };

  const onDownloadContracts = (currentTabNumber: number) => {
    dispatch(
      downloadContractAction({
        companyId: toNumber(companyId),
        ids: selectedContractsIds,
        type: contractsTypes[currentTabNumber],
      }),
    );
  };

  return (
    <Box data-gtm-page="contracts-list-view">
      <UPSnackBar
        open={downloadContractsError}
        onClose={() => dispatch(downloadContractsErrorAction(false))}
        content={t(CONTRACTS__EXPORT_FAILED)}
        type="error"
        vertical="bottom"
        horizontal="right"
        index={0}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <TabsContainerContractsListView value={currentTab} onChange={onChangeCurrentTab}>
          <TabStyled
            label={t(CONTRACTS__FUTURE_CONTRACTS)}
            onClick={() => {
              setCurrentTab(0);
              setViewType(ContractListViewTypes.Future);
            }}
            id="contracts-list-view-future-tab"
          />
          <TabStyled
            label={t(CONTRACTS__TO_BE_CONFIRMED)}
            onClick={() => {
              setCurrentTab(1);
              setViewType(ContractListViewTypes.ToBeConfirmed);
            }}
            data-gtm-event="contracts-list-view-to-be-confirmed-tab"
          />
          <TabStyled
            label={t(CONTRACTS__ARCHIVED)}
            onClick={() => {
              setCurrentTab(2);
              setViewType(ContractListViewTypes.Archived);
            }}
            data-gtm-event="contracts-list-view-completed-tab"
            id="contracts-list-view-archived-tab"
          />
          {canAccessToBeProcessed && (
            <TabStyled
              label={t(CONTRACTS__TO_BE_PROCESSED)}
              onClick={() => {
                setCurrentTab(3);
              }}
            />
          )}
          {canAccessToBeProcessed && (
            <TabStyled
              label={t(CONTRACTS__COMPLETED_NEW)}
              onClick={() => {
                setCurrentTab(4);
              }}
            />
          )}
        </TabsContainerContractsListView>

        {currentTab !== 3 && <DownloadIcon className="documentsIcon" onClick={() => onDownloadContracts(currentTab)} />}
      </Box>
      {currentTab === 0 && (
        <FutureContractsView
          makeDeleteRequest={makeDeleteRequest}
          costCentersList={costCentersList}
          functionsList={functionsList}
          currentWeek={currentWeek}
          currentYear={currentYear}
          setSelectedContractsId={setSelectedContractsIds}
          filters={filters}
          companyId={toNumber(companyId)}
        />
      )}
      {currentTab === 1 && (
        <ToBeConfirmedContractsView
          makeDeleteRequest={makeDeleteRequest}
          setSelectedContractsId={setSelectedContractsIds}
          costCentersList={costCentersList}
          functionsList={functionsList}
          currentWeek={currentWeek}
          currentYear={currentYear}
          filters={filters}
          companyId={toNumber(companyId)}
        />
      )}
      {currentTab === 2 && (
        <ArchivedContractsView
          makeDeleteRequest={makeDeleteRequest}
          costCentersList={costCentersList}
          functionsList={functionsList}
          setSelectedContractsId={setSelectedContractsIds}
          filters={filters}
          companyId={toNumber(companyId)}
        />
      )}
      {currentTab === 3 && canAccessToBeProcessed && (
        <ToBeProcessedView
          companyId={toNumber(companyId)}
          week={currentWeek}
          year={currentYear}
          date={filters.date}
          employeeIds={filters.employeeIds}
          costCenterId={filters.costCenterId}
          locationId={locationId}
          workerClass={workerClass}
          functionId={functionId}
        />
      )}
      {currentTab === 4 && canAccessToBeProcessed && (
        <CompletedView
          companyId={toNumber(companyId)}
          weeks={completedWeeks}
          date={filters.date}
          employeeIds={filters.employeeIds}
          costCenterId={filters.costCenterId}
          locationId={locationId}
          workerClass={workerClass}
          functionId={functionId}
          setSelectedContractsId={setSelectedContractsIds}
        />
      )}
    </Box>
  );
};

export default ContractsListView;
